import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Phone, MessageCircle, CheckCircle, Star, Clock, Shield, ChevronDown, Camera, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';

const Section = styled.section`
  padding: 5rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 3rem;
  font-size: clamp(2rem, 4vw, 3rem);
  color: ${brandTheme.colors.secondary[900]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin: 3rem 0;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[600]} 0%, ${brandTheme.colors.primary[700]} 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: white;
  }

  h3 {
    color: ${brandTheme.colors.primary[600]};
    margin-bottom: 1rem;
    font-size: 1.5rem;
  }

  p {
    color: ${brandTheme.colors.neutral.gray[600]};
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }

  .icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[600]} 0%, ${brandTheme.colors.primary[700]} 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    color: white;
  }

  h4 {
    color: ${brandTheme.colors.secondary[900]};
    margin-bottom: 0.5rem;
    font-size: 1.25rem;
  }

  p {
    color: ${brandTheme.colors.neutral.gray[600]};
    font-size: 0.9rem;
  }
`;

const PostcodesSection = styled.section`
  background: ${brandTheme.colors.neutral.gray[50]};
  padding: 5rem 0;
`;

const PostcodesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin: 3rem 0;
`;

const PostcodeCard = styled(motion.div)`
  background: white;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${brandTheme.colors.primary[600]};
    transform: translateY(-2px);
  }

  .postcode {
    font-size: 1.1rem;
    font-weight: bold;
    color: ${brandTheme.colors.primary[600]};
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[600]} 0%, ${brandTheme.colors.primary[700]} 100%);
  color: white;
  padding: 5rem 0;
  text-align: center;
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const StyledButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0.875rem 1.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.5);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const TestimonialsSection = styled.section`
  padding: 5rem 0;
  background: white;
`;

const TestimonialCard = styled(motion.div)`
  background: ${brandTheme.colors.neutral.gray[50]};
  border-radius: 15px;
  padding: 2rem;
  margin: 1rem 0;
  border-left: 4px solid ${brandTheme.colors.primary[600]};

  .stars {
    display: flex;
    gap: 0.25rem;
    margin-bottom: 1rem;
    color: #fbbf24;
  }

  .text {
    font-style: italic;
    margin-bottom: 1rem;
    color: ${brandTheme.colors.neutral.gray[700]};
  }

  .author {
    font-weight: 600;
    color: ${brandTheme.colors.secondary[900]};
  }
`;

const FAQSection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, ${brandTheme.colors.neutral.gray[50]} 0%, ${brandTheme.colors.primary[50]} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="%23f3f4f6" opacity="0.3"/><circle cx="75" cy="75" r="1" fill="%23f3f4f6" opacity="0.3"/><circle cx="50" cy="10" r="0.5" fill="%23f3f4f6" opacity="0.2"/><circle cx="10" cy="60" r="0.5" fill="%23f3f4f6" opacity="0.2"/><circle cx="90" cy="40" r="0.5" fill="%23f3f4f6" opacity="0.2"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.1;
  }
`;

const FAQContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }
`;

const FAQHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  .subtitle {
    font-size: 1.1rem;
    color: ${brandTheme.colors.primary[600]};
    font-weight: 600;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .title {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    color: ${brandTheme.colors.secondary[900]};
    margin-bottom: 1rem;
    font-weight: 700;
    line-height: 1.2;
  }
  
  .description {
    font-size: 1.2rem;
    color: ${brandTheme.colors.neutral.gray[600]};
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    margin-bottom: 3rem;
    
    .subtitle {
      font-size: 1rem;
    }
    
    .title {
      font-size: clamp(2rem, 6vw, 2.5rem);
    }
    
    .description {
      font-size: 1.1rem;
      padding: 0 1rem;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 2.5rem;
    
    .subtitle {
      font-size: 0.9rem;
    }
    
    .title {
      font-size: clamp(1.75rem, 7vw, 2rem);
    }
    
    .description {
      font-size: 1rem;
      padding: 0 0.5rem;
    }
  }
`;

const FAQItem = styled(motion.div)`
  background: white;
  border-radius: 20px;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.primary[600]} 50%, ${brandTheme.colors.primary[500]} 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 2rem 2.5rem;
  background: white;
  border: none;
  text-align: left;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${brandTheme.colors.secondary[900]};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  position: relative;
  word-wrap: break-word;
  overflow-wrap: break-word;

  &:hover {
    background: linear-gradient(135deg, ${brandTheme.colors.primary[50]} 0%, ${brandTheme.colors.neutral.gray[50]} 100%);
    color: ${brandTheme.colors.primary[700]};
  }

  .icon {
    transition: all 0.3s ease;
    color: ${brandTheme.colors.primary[600]};
    background: ${brandTheme.colors.primary[100]};
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &.active {
    background: linear-gradient(135deg, ${brandTheme.colors.primary[50]} 0%, ${brandTheme.colors.neutral.gray[50]} 100%);
    color: ${brandTheme.colors.primary[700]};
    
    .icon {
      background: ${brandTheme.colors.primary[600]};
      color: white;
      transform: rotate(180deg);
    }
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem;
    font-size: 1.1rem;

    .icon {
      width: 36px;
      height: 36px;
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem 1.25rem;
    font-size: 1rem;
    line-height: 1.4;

    .icon {
      width: 32px;
      height: 32px;
    }
  }
`;

const FAQAnswer = styled(motion.div)`
  padding: 1.5rem 2.5rem 2rem 2.5rem;
  color: ${brandTheme.colors.neutral.gray[700]};
  line-height: 1.7;
  border-top: 1px solid ${brandTheme.colors.neutral.gray[200]};
  background: linear-gradient(135deg, ${brandTheme.colors.neutral.gray[50]} 0%, white 100%);
  position: relative;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 1rem 1.5rem 1.5rem 1.5rem;
    font-size: 0.95rem;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    padding: 1rem 1.25rem 1.25rem 1.25rem;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const GallerySection = styled.section`
  padding: 5rem 0;
  background: white;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  img {
    width: 100%;
    height: 250px;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ImageModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  backdrop-filter: blur(10px);
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem 0;
  text-align: center;
`;

const ModalDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  text-align: center;
  max-width: 600px;
  line-height: 1.5;
  margin: 0 0 2rem 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    width: 40px;
    height: 40px;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-50%) scale(1.1);
  }

  &.prev {
    left: 2rem;
  }

  &.next {
    right: 2rem;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    
    &.prev {
      left: 1rem;
    }

    &.next {
      right: 1rem;
    }
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    bottom: 1rem;
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
`;


const ProcessSection = styled.section`
  padding: 5rem 0;
  background: ${brandTheme.colors.neutral.gray[50]};
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2rem;
  margin: 3rem 0;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 1000px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  position: relative;

  .step-number {
    position: absolute;
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 30px;
    height: 30px;
    background: ${brandTheme.colors.primary[600]};
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.9rem;
  }

  .icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[600]} 0%, ${brandTheme.colors.primary[700]} 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1rem auto 1.5rem;
    color: white;
  }

  h4 {
    color: ${brandTheme.colors.secondary[900]};
    margin-bottom: 1rem;
    font-size: 1.25rem;
  }

  p {
    color: ${brandTheme.colors.neutral.gray[600]};
    line-height: 1.6;
  }
`;

const KingstonPage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openImageModal = (index: number) => {
    setSelectedImageIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImageIndex(null);
  };

  const goToPreviousImage = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex(selectedImageIndex === 0 ? galleryItems.length - 1 : selectedImageIndex - 1);
    }
  };

  const goToNextImage = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex(selectedImageIndex === galleryItems.length - 1 ? 0 : selectedImageIndex + 1);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (selectedImageIndex !== null) {
      if (event.key === 'Escape') {
        closeImageModal();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPreviousImage();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNextImage();
      }
    }
  };

  // Add keyboard event listener when modal is open
  useEffect(() => {
    if (selectedImageIndex !== null) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImageIndex]);

  const services = [
    {
      title: "Resin Driveways",
      description: "Premium permeable driveways that enhance your property's curb appeal while meeting SUDS requirements.",
      icon: <MapPin size={24} />
    },
    {
      title: "Garden Pathways",
      description: "Beautiful resin bound pathways that guide visitors through your garden with style and durability.",
      icon: <MapPin size={24} />
    },
    {
      title: "Pool Decks",
      description: "Non-slip resin bound surfacing perfect for pool areas, combining safety with aesthetic appeal.",
      icon: <MapPin size={24} />
    },
    {
      title: "Commercial Surfacing",
      description: "Heavy-duty resin bound solutions for commercial properties, car parks, and public spaces.",
      icon: <MapPin size={24} />
    }
  ];

  const features = [
    {
      title: "10+ Year Warranty",
      description: "All our resin bound installations come with a comprehensive warranty for your peace of mind.",
      icon: <Shield size={24} />
    },
    {
      title: "Free Survey",
      description: "We provide complimentary site surveys across all Kingston postcodes with no obligation.",
      icon: <CheckCircle size={24} />
    },
    {
      title: "Quick Installation",
      description: "Most projects completed within 1-2 days, minimizing disruption to your daily routine.",
      icon: <Clock size={24} />
    },
    {
      title: "5-Star Reviews",
      description: "Rated 5 stars by our customers across Kingston and surrounding areas.",
      icon: <Star size={24} />
    }
  ];

  const testimonials = [
    {
      text: "Excellent service from start to finish. Our new resin driveway looks fantastic and the team was professional and clean.",
      author: "Sarah M., Mayfair",
      rating: 5
    },
    {
      text: "The resin bound pathway has transformed our garden. It's been 3 years and still looks as good as new.",
      author: "James L., Marylebone",
      rating: 5
    },
    {
      text: "Great value for money and excellent customer service. Highly recommend for anyone in Kingston.",
      author: "Emma R., Soho",
      rating: 5
    }
  ];

  const postcodes = [
    // KT1 (Kingston upon Thames)
    "KT1", "KT1 0", "KT1 1", "KT1 2", "KT1 3", "KT1 4", "KT1 5", "KT1 6", "KT1 7", "KT1 8", "KT1 9",
    // KT2 (Kingston upon Thames)
    "KT2", "KT2 0", "KT2 1", "KT2 2", "KT2 3", "KT2 4", "KT2 5", "KT2 6", "KT2 7", "KT2 8", "KT2 9",
    // KT3 (New Malden)
    "KT3", "KT3 0", "KT3 1", "KT3 2", "KT3 3", "KT3 4", "KT3 5", "KT3 6", "KT3 7", "KT3 8", "KT3 9",
    // KT4 (Worcester Park)
    "KT4", "KT4 0", "KT4 1", "KT4 2", "KT4 3", "KT4 4", "KT4 5", "KT4 6", "KT4 7", "KT4 8", "KT4 9",
    // KT5 (Surbiton)
    "KT5", "KT5 0", "KT5 1", "KT5 2", "KT5 3", "KT5 4", "KT5 5", "KT5 6", "KT5 7", "KT5 8", "KT5 9",
    // KT6 (Surbiton)
    "KT6", "KT6 0", "KT6 1", "KT6 2", "KT6 3", "KT6 4", "KT6 5", "KT6 6", "KT6 7", "KT6 8", "KT6 9",
    // KT7 (Thames Ditton)
    "KT7", "KT7 0", "KT7 1", "KT7 2", "KT7 3", "KT7 4", "KT7 5", "KT7 6", "KT7 7", "KT7 8", "KT7 9",
    // KT8 (West Molesey)
    "KT8", "KT8 0", "KT8 1", "KT8 2", "KT8 3", "KT8 4", "KT8 5", "KT8 6", "KT8 7", "KT8 8", "KT8 9",
    // KT9 (Chessington)
    "KT9", "KT9 0", "KT9 1", "KT9 2", "KT9 3", "KT9 4", "KT9 5", "KT9 6", "KT9 7", "KT9 8", "KT9 9",
    // KT10 (Esher)
    "KT10", "KT10 0", "KT10 1", "KT10 2", "KT10 3", "KT10 4", "KT10 5", "KT10 6", "KT10 7", "KT10 8", "KT10 9",
    // KT11 (Cobham)
    "KT11", "KT11 0", "KT11 1", "KT11 2", "KT11 3", "KT11 4", "KT11 5", "KT11 6", "KT11 7", "KT11 8", "KT11 9",
    // KT12 (Walton-on-Thames)
    "KT12", "KT12 0", "KT12 1", "KT12 2", "KT12 3", "KT12 4", "KT12 5", "KT12 6", "KT12 7", "KT12 8", "KT12 9",
    // KT13 (Weybridge)
    "KT13", "KT13 0", "KT13 1", "KT13 2", "KT13 3", "KT13 4", "KT13 5", "KT13 6", "KT13 7", "KT13 8", "KT13 9",
    // KT14 (West Byfleet)
    "KT14", "KT14 0", "KT14 1", "KT14 2", "KT14 3", "KT14 4", "KT14 5", "KT14 6", "KT14 7", "KT14 8", "KT14 9",
    // KT15 (Addlestone)
    "KT15", "KT15 0", "KT15 1", "KT15 2", "KT15 3", "KT15 4", "KT15 5", "KT15 6", "KT15 7", "KT15 8", "KT15 9",
    // KT16 (Chertsey)
    "KT16", "KT16 0", "KT16 1", "KT16 2", "KT16 3", "KT16 4", "KT16 5", "KT16 6", "KT16 7", "KT16 8", "KT16 9",
    // KT17 (Epsom)
    "KT17", "KT17 0", "KT17 1", "KT17 2", "KT17 3", "KT17 4", "KT17 5", "KT17 6", "KT17 7", "KT17 8", "KT17 9",
    // KT18 (Epsom)
    "KT18", "KT18 0", "KT18 1", "KT18 2", "KT18 3", "KT18 4", "KT18 5", "KT18 6", "KT18 7", "KT18 8", "KT18 9",
    // KT19 (Epsom)
    "KT19", "KT19 0", "KT19 1", "KT19 2", "KT19 3", "KT19 4", "KT19 5", "KT19 6", "KT19 7", "KT19 8", "KT19 9",
    // KT20 (Tadworth)
    "KT20", "KT20 0", "KT20 1", "KT20 2", "KT20 3", "KT20 4", "KT20 5", "KT20 6", "KT20 7", "KT20 8", "KT20 9",
    // KT21 (Ashtead)
    "KT21", "KT21 0", "KT21 1", "KT21 2", "KT21 3", "KT21 4", "KT21 5", "KT21 6", "KT21 7", "KT21 8", "KT21 9",
    // KT22 (Leatherhead)
    "KT22", "KT22 0", "KT22 1", "KT22 2", "KT22 3", "KT22 4", "KT22 5", "KT22 6", "KT22 7", "KT22 8", "KT22 9",
    // KT23 (Leatherhead)
    "KT23", "KT23 0", "KT23 1", "KT23 2", "KT23 3", "KT23 4", "KT23 5", "KT23 6", "KT23 7", "KT23 8", "KT23 9",
    // KT24 (Leatherhead)
    "KT24", "KT24 0", "KT24 1", "KT24 2", "KT24 3", "KT24 4", "KT24 5", "KT24 6", "KT24 7", "KT24 8", "KT24 9"
  ];

  const galleryItems = [
    {
      image: "/images/resin-surfaces-bound-work1.jpeg",
      title: "Kingston Driveway",
      description: "Premium granite resin bound driveway in historic Kingston location"
    },
    {
      image: "/images/resin-surfaces-bound-work2.jpeg",
      title: "Surbiton Pathway",
      description: "Elegant quartz pathway leading to Victorian townhouse"
    },
    {
      image: "/images/resin-surfaces-bound-work3.jpeg",
      title: "New Malden Commercial",
      description: "Durable resin bound surfacing for high-traffic commercial area"
    },
    {
      image: "/images/resin-surfaces-bound-work4.jpeg",
      title: "Thames Ditton Courtyard",
      description: "Beautiful courtyard transformation with natural stone aggregates"
    },
    {
      image: "/images/resin-surfaces-bound-work5.jpeg",
      title: "Chessington Pool Deck",
      description: "Anti-slip resin bound pool deck with safety features"
    },
    {
      image: "/images/resin-surfaces-bound-work6.jpeg",
      title: "Esher Garden Path",
      description: "Permeable pathway enhancing garden aesthetics"
    },
    {
      image: "/images/resin-surfaces-bound-work7.jpeg",
      title: "Weybridge Heritage Area",
      description: "Heritage-compliant surfacing near historic landmarks"
    },
    {
      image: "/images/resin-surfaces-bound-work8.jpeg",
      title: "Walton-on-Thames Road",
      description: "Luxury residential driveway with premium finish"
    }
  ];


  const processSteps = [
    {
      step: 1,
      title: "Free Survey",
      description: "We visit your Kingston property to assess the area and discuss your requirements.",
      icon: <MapPin size={24} />
    },
    {
      step: 2,
      title: "Design & Quote",
      description: "We create a detailed design and provide a comprehensive quote with no hidden costs.",
      icon: <Camera size={24} />
    },
    {
      step: 3,
      title: "Preparation",
      description: "We prepare the existing surface and ensure proper drainage for optimal results.",
      icon: <Shield size={24} />
    },
    {
      step: 4,
      title: "Installation",
      description: "Our expert team installs your resin bound surface with precision and care.",
      icon: <CheckCircle size={24} />
    },
    {
      step: 5,
      title: "Completion",
      description: "We clean up thoroughly and provide maintenance guidance for long-lasting results.",
      icon: <Star size={24} />
    }
  ];

  const faqs = [
            {
              question: "How much does a resin driveway cost in Kingston?",
              answer: "Resin driveway costs in Kingston typically range from £80-£120 per square metre, depending on the size, complexity, and chosen aggregates. For a standard single-car driveway (approximately 20m²), you can expect to pay between £1,600-£2,400. Our team provides free, no-obligation quotes for all Kingston postcodes."
            },
            {
              question: "Do I need planning permission for a resin driveway in Kingston?",
              answer: "Most residential resin driveways in Kingston don't require planning permission, especially if they're permeable and meet SUDS (Sustainable Urban Drainage Systems) requirements. However, if your property is in a conservation area or has specific restrictions, we'll advise you on the necessary permissions during our free survey."
            },
            {
              question: "How long does resin driveway installation take in Kingston?",
              answer: "Most resin driveway installations in Kingston are completed within 1-2 days. The exact timeframe depends on the size and complexity of your project. We work efficiently to minimize disruption to your daily routine and keep you informed throughout the process."
            },
            {
              question: "Is resin bound surfacing suitable for Kingston's weather?",
              answer: "Yes, resin bound surfacing is excellent for Kingston's climate. It's UV stable, frost resistant, and won't crack in cold weather. The permeable nature also helps with drainage during heavy rainfall, making it ideal for London's weather conditions."
            },
            {
              question: "Can I choose different colours and finishes for my Kingston driveway?",
              answer: "Absolutely! We offer a wide range of natural stone aggregates in various colours, sizes, and finishes. From classic granite to contemporary quartz, we can create a unique look that complements your Kingston property's architecture and your personal style."
            },
            {
              question: "What maintenance does a resin driveway require?",
              answer: "Resin driveways require minimal maintenance. Regular sweeping and occasional gentle pressure washing are usually sufficient. We provide detailed care instructions and offer maintenance services to keep your driveway looking pristine for years to come."
            },
            {
              question: "Do you work in all Kingston postcodes?",
              answer: "Yes, we provide resin bound surfacing services across all Kingston postcodes including KT1, KT2, KT3, KT4, KT5, KT6, KT7, KT8, KT9, KT10, KT11, KT12, KT13, KT14, KT15, KT16, KT17, KT18, KT19, KT20, KT21, KT22, KT23, KT24, and surrounding areas. Our team covers Kingston, Surbiton, New Malden, Thames Ditton, Chessington, Esher, Weybridge, and all other Kingston locations."
            },
            {
              question: "What warranty do you offer on resin driveways?",
              answer: "We offer a comprehensive 10+ year warranty on all our resin bound installations. This covers materials and workmanship, giving you complete peace of mind. Our warranty is backed by our partnership with Vuba Surfaces and GCL Products, industry-leading manufacturers."
            },
            {
              question: "How does resin bound compare to other driveway materials?",
              answer: "Resin bound surfacing offers superior benefits compared to traditional materials. It's permeable (SUDS compliant), anti-slip, low maintenance, and provides excellent durability. Unlike tarmac or concrete, it won't crack or fade, and it's more environmentally friendly due to its permeable nature."
            },
            {
              question: "Can you install resin driveways in conservation areas?",
              answer: "Yes, we have extensive experience working in Kingston's conservation areas including historic areas around Kingston upon Thames and Hampton Court. We understand the specific requirements and can advise on planning permissions and suitable materials that meet conservation area guidelines while providing modern functionality."
            }
  ];

  return (
    <>
      <Helmet>
                <title>🏆 #1 Resin Driveways Kingston | Premium Resin Bound Surfacing South West London | Surbiton, New Malden, Thames Ditton | Free Quotes</title>
                <meta name="description" content="🏆 #1 Resin Driveways Kingston specialists! Premium resin bound surfacing in South West London's most historic borough. Expert installation in Kingston, Surbiton, New Malden, Thames Ditton, Chessington, Esher, Weybridge. Partnered with Vuba Surfaces & GCL Products. 10+ year warranty, SUDS compliant, permeable, anti-slip surfaces. FREE quotes today!" />
                <meta name="keywords" content="resin driveways Kingston, resin bound surfacing Kingston, resin pathways Kingston, resin pool decks Kingston, resin driveways Surbiton, resin bound surfacing New Malden, resin driveways Thames Ditton, resin surfacing Chessington, resin driveways Esher, resin bound surfacing Weybridge, resin driveway installers Kingston, resin surfacing contractors Kingston, resin bound specialists Kingston, permeable driveways Kingston, SUDS compliant surfacing Kingston, anti-slip resin surfaces Kingston, resin driveway cost Kingston, resin driveway quotes Kingston, best resin driveway company Kingston, resin driveway near me Kingston, resin surfacing South West London, resin bound installation South West London, commercial resin surfacing Kingston, residential resin driveways Kingston, resin driveway design Kingston, resin bound maintenance Kingston, resin surface repair Kingston, resin bound vs tarmac Kingston, resin driveway benefits Kingston, resin surfacing experts Kingston, premium resin driveways Kingston, resin bound installation process Kingston, resin driveways KT1, resin bound surfacing KT2, resin driveways KT3, resin surfacing KT4, resin bound KT5, resin driveways KT6, resin surfacing KT7, resin bound KT8, resin driveways KT9, resin surfacing KT10, resin bound KT11, resin driveways KT12, resin surfacing KT13, resin bound KT14, resin driveways KT15, resin surfacing KT16, resin bound KT17, resin driveways KT18, resin surfacing KT19, resin bound KT20, resin driveways KT21, resin surfacing KT22, resin bound KT23, resin driveways KT24, resin driveway installers South West London, resin surfacing contractors South West London, resin bound specialists South West London, permeable driveways South West London, SUDS compliant surfacing South West London, anti-slip resin surfaces South West London, resin driveway cost South West London, resin driveway quotes South West London, best resin driveway company South West London, resin driveway near me South West London, resin surfacing London, resin bound installation London, commercial resin surfacing London, residential resin driveways London, resin driveway design London, resin bound maintenance London, resin surface repair London, resin bound vs tarmac London, resin driveway benefits London, resin surfacing experts London, premium resin driveways London, resin bound installation process London" />
                <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/resin-driveways-kingston/" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <meta name="author" content="Resin Surfaces London" />
        <meta name="publisher" content="Resin Surfaces London" />
        <meta name="copyright" content="Resin Surfaces London" />
        <meta name="language" content="en-GB" />
                <meta name="geo.region" content="GB-LND" />
                <meta name="geo.placename" content="Kingston upon Thames, London" />
                <meta name="geo.position" content="51.4123;-0.3004" />
                <meta name="ICBM" content="51.4123, -0.3004" />
        <meta name="theme-color" content="#1e40af" />
        <meta name="msapplication-TileColor" content="#1e40af" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/resin-driveways-kingston/" />
                <meta property="og:title" content="🏆 #1 Resin Driveways Kingston | Premium Resin Bound Surfacing South West London | Surbiton, New Malden, Thames Ditton | Free Quotes" />
                <meta property="og:description" content="🏆 #1 Resin Driveways Kingston specialists! Premium resin bound surfacing in South West London's most historic borough. Expert installation in Kingston, Surbiton, New Malden, Thames Ditton, Chessington, Esher, Weybridge. Partnered with Vuba Surfaces & GCL Products. 10+ year warranty, SUDS compliant, permeable, anti-slip surfaces. FREE quotes today!" />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content="Premium resin bound driveway installation in Kingston upon Thames, London" />
        <meta property="og:site_name" content="Resin Surfaces London" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:url" content="https://www.resinsurfaceslondon.co.uk/resin-driveways-kingston/" />
                <meta property="twitter:title" content="🏆 #1 Resin Driveways Kingston | Premium Resin Bound Surfacing South West London | Surbiton, New Malden, Thames Ditton | Free Quotes" />
                <meta property="twitter:description" content="🏆 #1 Resin Driveways Kingston specialists! Premium resin bound surfacing in South West London's most historic borough. Expert installation in Kingston, Surbiton, New Malden, Thames Ditton, Chessington, Esher, Weybridge. Partnered with Vuba Surfaces & GCL Products. 10+ year warranty, SUDS compliant, permeable, anti-slip surfaces. FREE quotes today!" />
        <meta property="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
                <meta property="twitter:image:alt" content="Premium resin bound driveway installation in Kingston upon Thames, London" />
        <meta property="twitter:creator" content="@resinsurfaceslondon" />
        <meta property="twitter:site" content="@resinsurfaceslondon" />

        {/* Structured Data - Local Business */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
                    "name": "Resin Surfaces London - Kingston",
                    "alternateName": "Resin Driveways Kingston",
                    "description": "South West London's most historic borough, home to Kingston upon Thames, Surbiton, and New Malden. We provide premium resin bound surfacing for residential and commercial properties.",
                    "url": "https://www.resinsurfaceslondon.co.uk/resin-driveways-kingston/",
            "telephone": "+447535935779",
            "email": "info@resinsurfaceslondon.co.uk",
                    "address": {
                      "@type": "PostalAddress",
                      "streetAddress": "Kingston upon Thames",
                      "addressLocality": "London",
                      "postalCode": "KT1",
                      "addressCountry": "GB"
                    },
                    "geo": {
                      "@type": "GeoCoordinates",
                      "latitude": "51.4123",
                      "longitude": "-0.3004"
                    },
                    "areaServed": [
                      "Kingston", "Surbiton", "New Malden", "Thames Ditton", "Chessington", "Esher", "Weybridge", "KT1", "KT2", "KT3", "KT4", "KT5", "KT6", "KT7", "KT8", "KT9", "KT10", "KT11", "KT12", "KT13", "KT14", "KT15", "KT16", "KT17", "KT18", "KT19", "KT20", "KT21", "KT22", "KT23", "KT24"
                    ],
            "serviceType": "Resin Bound Surfacing",
            "priceRange": "£££",
            "openingHours": "Mo-Fr 08:00-18:00, Sa 09:00-17:00",
            "paymentAccepted": "Cash, Credit Card, Bank Transfer",
            "currenciesAccepted": "GBP",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Resin Bound Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Resin Driveways",
                    "description": "Premium permeable driveways that enhance your property's curb appeal while meeting SUDS requirements."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Garden Pathways",
                    "description": "Beautiful resin bound pathways that guide visitors through your garden with style and durability."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Pool Decks",
                    "description": "Non-slip resin bound surfacing perfect for pool areas, combining safety with aesthetic appeal."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Commercial Surfacing",
                    "description": "Heavy-duty resin bound solutions for commercial properties, car parks, and public spaces."
                  }
                }
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "150",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "author": {
                  "@type": "Person",
                  "name": "Sarah M."
                },
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "reviewBody": "Exceptional service and beautiful results. Our Mayfair driveway looks stunning and the team was professional throughout."
              },
              {
                "@type": "Review",
                "author": {
                  "@type": "Person",
                  "name": "James R."
                },
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "reviewBody": "Outstanding work in Marylebone. The resin bound surface is perfect and the installation was completed on time."
              }
            ]
          })}
        </script>

        {/* Structured Data - FAQ */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                        "name": "How much does a resin driveway cost in Kingston?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Resin driveway costs in Kingston typically range from £80-£120 per square metre, depending on the size, complexity, and chosen aggregates. For a standard single-car driveway (approximately 20m²), you can expect to pay between £1,600-£2,400. Our team provides free, no-obligation quotes for all Kingston postcodes."
                        }
              },
              {
                "@type": "Question",
                        "name": "Do I need planning permission for a resin driveway in Kingston?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Most residential resin driveways in Kingston don't require planning permission, especially if they're permeable and meet SUDS (Sustainable Urban Drainage Systems) requirements. However, if your property is in a conservation area or has specific restrictions, we'll advise you on the necessary permissions during our free survey."
                        }
              },
              {
                "@type": "Question",
                        "name": "How long does resin driveway installation take in Kingston?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Most resin driveway installations in Kingston are completed within 1-2 days. The exact timeframe depends on the size and complexity of your project. We work efficiently to minimize disruption to your daily routine and keep you informed throughout the process."
                        }
              }
            ]
          })}
        </script>

        {/* Structured Data - Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.resinsurfaceslondon.co.uk/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Areas We Cover",
                "item": "https://www.resinsurfaceslondon.co.uk/areas/"
              },
              {
                "@type": "ListItem",
                "position": 3,
                        "name": "Kingston",
                        "item": "https://www.resinsurfaceslondon.co.uk/resin-driveways-kingston/"
              }
            ]
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Resin Driveways Kingston"
        subtitle="Premium resin bound surfacing specialists serving Kingston and surrounding areas"
        badge="Local Experts • Professional Installation • 10+ Year Warranty"
        backgroundImage="/images/resin-surfaces-bound-work2.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <Section>
        <Container>
          <SectionTitle>Why Choose Resin Surfaces London for Kingston?</SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.2rem', color: brandTheme.colors.neutral.gray[600], marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem' }}>
            South West London's most historic borough, home to Kingston upon Thames, Surbiton, and New Malden. We provide premium resin bound surfacing for residential and commercial properties.
          </p>
          
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{feature.icon}</div>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Container>
      </Section>

      <Section style={{ background: brandTheme.colors.primary[50] }}>
        <Container>
          <SectionTitle>Kingston-Specific Benefits</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '3rem' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              style={{ background: 'white', padding: '2rem', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <h3 style={{ color: brandTheme.colors.primary[600], marginBottom: '1rem' }}>Conservation Area Expertise</h3>
              <p style={{ color: brandTheme.colors.neutral.gray[600], lineHeight: 1.6 }}>
                We understand Kingston's conservation area requirements and can advise on planning permissions and suitable materials that meet heritage guidelines while providing modern functionality.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              style={{ background: 'white', padding: '2rem', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <h3 style={{ color: brandTheme.colors.primary[600], marginBottom: '1rem' }}>Luxury Property Experience</h3>
              <p style={{ color: brandTheme.colors.neutral.gray[600], lineHeight: 1.6 }}>
                Our team has extensive experience working on high-end properties in Mayfair, Marylebone, and Soho, ensuring the highest standards of workmanship and attention to detail.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              style={{ background: 'white', padding: '2rem', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <h3 style={{ color: brandTheme.colors.primary[600], marginBottom: '1rem' }}>SUDS Compliance</h3>
              <p style={{ color: brandTheme.colors.neutral.gray[600], lineHeight: 1.6 }}>
                All our resin bound surfaces are fully SUDS compliant, helping Kingston meet its environmental obligations while providing beautiful, functional driveways and pathways.
              </p>
            </motion.div>
          </div>
        </Container>
      </Section>

      <Section style={{ background: brandTheme.colors.neutral.gray[50] }}>
        <Container>
          <SectionTitle>Our Services in Kingston</SectionTitle>
          <ServicesGrid>
            {services.map((service, index) => (
              <Card
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <StyledButton to="/contact/" className="primary">
                  Get Quote <ArrowRight size={16} />
                </StyledButton>
              </Card>
            ))}
          </ServicesGrid>
        </Container>
      </Section>

      <GallerySection>
        <Container>
          <SectionTitle>Our Kingston Work Gallery</SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.1rem', color: brandTheme.colors.neutral.gray[600], marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem' }}>
            Explore our portfolio of completed resin bound projects across Kingston's most historic areas
          </p>
          <GalleryGrid>
            {galleryItems.map((item, index) => (
              <GalleryItem
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => openImageModal(index)}
              >
                <img src={item.image} alt={item.title} />
              </GalleryItem>
            ))}
          </GalleryGrid>
        </Container>
      </GallerySection>

      <ProcessSection>
        <Container>
          <SectionTitle>Our Kingston Installation Process</SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.1rem', color: brandTheme.colors.neutral.gray[600], marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem' }}>
            From initial consultation to final completion, we ensure a seamless experience for every Kingston project
          </p>
          <ProcessGrid>
            {processSteps.map((step, index) => (
              <ProcessCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="step-number">{step.step}</div>
                <div className="icon">{step.icon}</div>
                <h4>{step.title}</h4>
                <p>{step.description}</p>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <TestimonialsSection>
        <Container>
          <SectionTitle>What Our Kingston Customers Say</SectionTitle>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="text">"{testimonial.text}"</p>
              <p className="author">- {testimonial.author}</p>
            </TestimonialCard>
          ))}
        </Container>
      </TestimonialsSection>

      <PostcodesSection>
        <Container>
          <SectionTitle>Areas We Cover in Kingston</SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.1rem', color: brandTheme.colors.neutral.gray[600], marginBottom: '2rem' }}>
            We provide resin bound surfacing services across all Kingston postcodes:
          </p>
          <PostcodesGrid>
            {postcodes.map((postcode, index) => (
              <PostcodeCard
                key={postcode}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.02 }}
                viewport={{ once: true }}
              >
                <div className="postcode">{postcode}</div>
              </PostcodeCard>
            ))}
          </PostcodesGrid>
        </Container>
      </PostcodesSection>

      <FAQSection>
        <Container>
          <FAQHeader>
            <div className="subtitle">Got Questions?</div>
            <div className="title">Frequently Asked Questions</div>
            <div className="description">
              Everything you need to know about resin bound surfacing in Kingston. Can't find what you're looking for? 
              <Link to="/contact/" style={{ color: brandTheme.colors.primary[600], textDecoration: 'none', fontWeight: '600' }}> Contact us directly</Link>.
            </div>
          </FAQHeader>
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <FAQQuestion
                  onClick={() => toggleFAQ(index)}
                  className={openFAQ === index ? 'active' : ''}
                >
                  <span>{faq.question}</span>
                  <ChevronDown size={20} className="icon" />
                </FAQQuestion>
                {openFAQ === index && (
                  <FAQAnswer
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    {faq.answer}
                  </FAQAnswer>
                )}
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>

      <CTASection>
        <Container>
          <SectionTitle style={{ color: 'white' }}>Ready to Transform Your Kingston Property?</SectionTitle>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.9, color: 'white' }}>
            Join hundreds of satisfied customers across Kingston who have chosen Resin Surfaces London
          </p>
          <CTAButtons>
            <StyledButton to="/contact/" className="secondary">
              Get Free Quote <ArrowRight size={20} />
            </StyledButton>
            <StyledButton to="https://wa.me/447535935779" className="secondary">
              <MessageCircle size={20} /> WhatsApp Chat
            </StyledButton>
          </CTAButtons>
          
          <div style={{ marginTop: '3rem', padding: '2rem', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '15px', backdropFilter: 'blur(10px)' }}>
            <h3 style={{ color: 'white', textAlign: 'center', marginBottom: '1.5rem', fontSize: '1.5rem' }}>
              🏆 Kingston's #1 Choice for Resin Bound Surfacing
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Free Kingston Survey</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>10+ Year Warranty</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>SUDS Compliant</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>5★ Rated Service</div>
              </div>
            </div>
          </div>
        </Container>
      </CTASection>

      {/* Image Modal */}
      {selectedImageIndex !== null && (
        <ImageModal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={closeImageModal}
        >
          <CloseButton onClick={closeImageModal}>
            <X size={24} />
          </CloseButton>
          
          <NavigationButton 
            className="prev" 
            onClick={goToPreviousImage}
            aria-label="Previous image"
          >
            <ChevronLeft size={24} />
          </NavigationButton>
          
          <NavigationButton 
            className="next" 
            onClick={goToNextImage}
            aria-label="Next image"
          >
            <ChevronRight size={24} />
          </NavigationButton>

          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalImage 
              src={galleryItems[selectedImageIndex].image} 
              alt={galleryItems[selectedImageIndex].title}
            />
            <ModalTitle>{galleryItems[selectedImageIndex].title}</ModalTitle>
            <ModalDescription>{galleryItems[selectedImageIndex].description}</ModalDescription>
          </ModalContent>

          <ImageCounter>
            {selectedImageIndex + 1} / {galleryItems.length}
          </ImageCounter>
        </ImageModal>
      )}
    </>
  );
};

export default KingstonPage;
